import React, { useMemo } from 'react';
import { TextField, OutlinedInputProps } from '@mui/material';
import cn from 'classnames';

import { useComputedStyleProperty } from '../../hooks';
import { InputProps } from '../types';
import { Included } from '../../../Icons';
import { useTheme } from '../../../theme';

const Input: React.FC<InputProps> = ({
	className,
	variant = 'labelIn',
	label,
	InputLabelProps,
	InputProps,
	FormHelperTextProps,
	isValid,
	classes,
	...restProps
}) => {
	const { Input: theme } = useTheme('BaseComponents');
	const { value: inputVariant, elRef } = useComputedStyleProperty('--variant', variant);

	const labelClasses = InputLabelProps?.classes;
	const inputClasses = InputProps?.classes;
	const helperTextClasses = FormHelperTextProps?.classes;

	const muiVariant = inputVariant === 'underline' ? 'filled' : 'outlined';

	const variantClasses = useMemo(() => {
		if (muiVariant === 'outlined') {
			return {
				notchedOutline: cn(
					theme.notchedOutline,
					(inputClasses as OutlinedInputProps['classes'])?.notchedOutline
				)
			} as OutlinedInputProps['classes'];
		}

		return {};
	}, [muiVariant]);

	return (
		<TextField
			className={cn(className, theme[inputVariant], {
				[classes?.underline]: inputVariant === 'underline',
				[classes?.labelIn]: inputVariant === 'labelIn',
				[classes?.labelOut]: inputVariant === 'labelOut',
				[classes?.labelOnLine]: inputVariant === 'labelOnLine',
				[classes?.withoutLabel]: !label,
				[theme.withoutLabel]: !label
			})}
			variant={muiVariant}
			label={label}
			InputLabelProps={{
				...InputLabelProps,
				classes: {
					root: cn(theme.label, labelClasses?.root),
					focused: cn(theme.focused, labelClasses?.focused),
					disabled: cn(theme.disabled, labelClasses?.disabled),
					error: cn(theme.error, labelClasses?.error),
					shrink: cn(theme.shrink, labelClasses?.shrink)
				}
			}}
			InputProps={{
				...InputProps,
				classes: {
					root: cn(theme.input, theme[muiVariant], inputClasses?.root, {
						[classes?.outlined]: muiVariant === 'outlined',
						[classes?.filled]: muiVariant === 'filled',
						[theme.valid]: isValid
					}),
					focused: cn(theme.focused, inputClasses?.focused),
					disabled: cn(theme.disabled, inputClasses?.disabled),
					error: cn(theme.error, inputClasses?.error),
					...variantClasses
				},
				endAdornment: InputProps?.endAdornment ? (
					<>
						{isValid && <div className={theme.valid_icon}>{Included}</div>}
						{InputProps.endAdornment}
					</>
				) : undefined
			}}
			FormHelperTextProps={{
				...FormHelperTextProps,
				classes: {
					root: cn(theme.helperText, helperTextClasses?.root),
					focused: cn(theme.focused, helperTextClasses?.root),
					disabled: cn(theme.disabled, helperTextClasses?.root),
					error: cn(theme.error, helperTextClasses?.error)
				}
			}}
			ref={elRef}
			{...restProps}
		/>
	);
};

export default Input;
